// components/AddResumeModal.jsx
// Light-theme resume upload modal — mirrors the onboarding ResumeUploadStep
// flow (pick → uploading (30s timer) → done / error) and the same
// POST {API_BASE}/users/resume multipart contract.
import { useEffect, useRef, useState } from "react"
import { X } from "lucide-react"

const API_BASE  = "https://jobscanner-e1dd4.ue.r.appspot.com"
const MAX_BYTES = 10 * 1024 * 1024
const FF = '"Plus Jakarta Sans", "DM Sans", -apple-system, BlinkMacSystemFont, sans-serif'

// Light palette — matches onboarding OB_COLORS.
const C = {
  bg:        "#FAFAFA",
  card:      "#FFFFFF",
  border:    "#E4E4E7",
  text:      "#18181b",
  textSec:   "#52525b",
  textMuted: "#71717a",
  textFaint: "#a1a1aa",
  cta:       "#18181b",
  ctaHov:    "#3f3f46",
}

export default function AddResumeModal({ userId, onClose, onUploaded }) {
  const [phase,    setPhase]    = useState("pick") // pick | uploading | done | error
  const [fileName, setFileName] = useState("")
  const [errorMsg, setErrorMsg] = useState("")
  const inputRef = useRef(null)
  const abortRef = useRef(null)

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && phase !== "uploading") onClose() }
    document.addEventListener("keydown", h)
    return () => document.removeEventListener("keydown", h)
  }, [onClose, phase])

  useEffect(() => () => { try { abortRef.current?.abort() } catch {} }, [])

  const onPickClick = () => inputRef.current?.click()

  const onFile = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    if (file.type && file.type !== "application/pdf") {
      setErrorMsg("Only PDF files are accepted")
      setPhase("error")
      return
    }
    if (file.size > MAX_BYTES) {
      setErrorMsg("File must be under 10 MB")
      setPhase("error")
      return
    }
    setFileName(file.name)
    setErrorMsg("")
    setPhase("uploading")
    await runUpload(file)
  }

  const runUpload = async (file) => {
    try {
      const form = new FormData()
      form.append("userId", userId)
      form.append("name", `resume_${Date.now()}`)
      form.append("resumeFile", file, file.name)

      const ctrl = new AbortController()
      abortRef.current = ctrl
      const timeout = setTimeout(() => ctrl.abort(), 90_000)

      let res, json
      try {
        res = await fetch(`${API_BASE}/users/resume`, {
          method: "POST",
          body: form,
          signal: ctrl.signal,
        })
      } catch (err) {
        clearTimeout(timeout)
        if (err?.name === "AbortError") {
          setErrorMsg("Request timed out. Check your connection.")
        } else {
          setErrorMsg("Upload failed. Please try again.")
        }
        setPhase("error")
        return
      }
      clearTimeout(timeout)

      try { json = await res.json() } catch { json = null }

      if (!res.ok || !json?.success) {
        setErrorMsg(json?.error || `Upload failed (${res.status})`)
        setPhase("error")
        return
      }

      setPhase("done")
      onUploaded?.()
    } catch {
      setErrorMsg("Upload failed. Please try again.")
      setPhase("error")
    }
  }

  const retry = () => {
    setPhase("pick")
    setErrorMsg("")
    setFileName("")
  }

  const canDismiss = phase !== "uploading"

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes arm-in   { from { opacity:0; transform:translateY(10px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }
        @keyframes arm-fade { from { opacity:0; } to { opacity:1; } }
        @keyframes arm-spin { to { transform: rotate(360deg); } }
        @keyframes arm-pop  { from { opacity:0; transform: translateY(4px); } to { opacity:1; transform: translateY(0); } }
        .arm-pop { animation: arm-pop 320ms ease forwards; }
      `}</style>

      <div
        onClick={e => { if (e.target === e.currentTarget && canDismiss) onClose() }}
        style={{
          position: "fixed", inset: 0, zIndex: 400,
          background: "rgba(24,24,27,0.45)",
          backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "0 16px",
          animation: "arm-fade 0.18s ease",
          fontFamily: FF,
        }}
      >
        <div style={{
          width: "100%", maxWidth: 420,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 22, padding: 20,
          animation: "arm-in 0.26s cubic-bezier(0.22,1,0.36,1)",
          boxShadow: "0 24px 60px rgba(24,24,27,0.18)",
          fontFamily: FF,
        }}>
          {/* Header */}
          <div style={{
            display: "flex", alignItems: "flex-start", justifyContent: "space-between",
            marginBottom: 16,
          }}>
            <div>
              <div style={{
                fontSize: 10, fontWeight: 800, letterSpacing: "0.1em",
                textTransform: "uppercase", color: C.textFaint, marginBottom: 4,
              }}>
                Resume
              </div>
              <div style={{
                fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: "-0.02em",
                lineHeight: 1.2,
              }}>
                {phase === "done"      ? "Résumé uploaded"
                 : phase === "error"   ? "Something went wrong"
                 : phase === "uploading" ? "Analysing your résumé"
                 : "Upload your résumé"}
              </div>
            </div>
            {canDismiss && (
              <button
                onClick={onClose}
                aria-label="Close"
                style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: C.card, border: `1px solid ${C.border}`,
                  cursor: "pointer", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  color: C.textMuted, flexShrink: 0,
                  transition: "background 0.15s, color 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.color = C.text }}
                onMouseLeave={e => { e.currentTarget.style.color = C.textMuted }}
              >
                <X size={14} />
              </button>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={onFile}
            style={{ display: "none" }}
          />

          {phase === "pick"      && <PickCard onClick={onPickClick} />}
          {phase === "uploading" && <UploadingCard fileName={fileName} />}
          {phase === "done"      && <DoneCard fileName={fileName} onClose={onClose} />}
          {phase === "error"     && <ErrorCard message={errorMsg || "Upload failed. Please try again."} onRetry={retry} onClose={onClose} />}
        </div>
      </div>
    </>
  )
}

function PickCard({ onClick }) {
  return (
    <div
      className="arm-pop"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick() }}
      style={{
        position: "relative",
        padding: 20,
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 20, cursor: "pointer",
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.textMuted }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border }}
    >
      <div style={{
        position: "absolute", top: 14, right: 14,
        padding: "3px 8px", background: C.text, color: "#fff",
        borderRadius: 6, fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
      }}>
        RECOMMENDED
      </div>
      <div style={{
        width: 46, height: 46, borderRadius: 11,
        background: "#F4F4F5",
        border: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.textSec, marginBottom: 16,
      }}>
        <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
          <path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"
            stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M12 2v4h4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          <path d="M7 10h6M7 13h6M7 7h2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 4 }}>
        Upload your résumé
      </div>
      <div style={{ fontSize: 13, color: C.textSec, lineHeight: 1.5 }}>
        PDF format, up to 10 MB. We'll extract everything automatically.
      </div>
    </div>
  )
}

function UploadingCard({ fileName }) {
  const [secs, setSecs] = useState(30)
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000)
    return () => clearInterval(id)
  }, [])
  const almostDone = secs === 0
  const label = fileName ? `Analyzing ${fileName}…` : "Analyzing résumé…"
  return (
    <div className="arm-pop" style={{
      padding: 20, background: C.card, border: `1px solid ${C.border}`,
      borderRadius: 20,
    }}>
      <div style={{ width: 46, height: 46, position: "relative" }}>
        {almostDone ? (
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none" style={{ animation: "arm-spin 0.9s linear infinite" }}>
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="2.5" />
            <path d="M12 2a10 10 0 0110 10" stroke={C.text} strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <>
            <svg width="46" height="46" viewBox="0 0 46 46">
              <circle cx="23" cy="23" r="20" stroke={C.border} strokeWidth="2.5" fill="none" />
              <circle
                cx="23" cy="23" r="20" stroke={C.text} strokeWidth="2.5" fill="none"
                strokeDasharray={`${(secs / 30) * 125.6} 125.6`}
                strokeLinecap="round"
                transform="rotate(-90 23 23)"
                style={{ transition: "stroke-dasharray 1s linear" }}
              />
            </svg>
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 13, fontWeight: 700, color: C.text,
            }}>
              {secs}
            </div>
          </>
        )}
      </div>
      <div style={{
        marginTop: 16, fontSize: 16, fontWeight: 700, color: C.text,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: C.textSec }}>
        {almostDone ? "Almost done…" : `It may take up to ${secs}s`}
      </div>
    </div>
  )
}

function DoneCard({ fileName, onClose }) {
  return (
    <div className="arm-pop" style={{
      padding: 20, background: "#F0F7F2",
      border: "1px solid #C2DCCA", borderRadius: 20,
    }}>
      <div style={{
        width: 46, height: 46, borderRadius: "50%",
        background: "rgba(42,82,53,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
          <path d="M4 10l5 5 7-7" stroke="#2A5235" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 700, color: C.text }}>
        Résumé uploaded
      </div>
      {fileName && (
        <div style={{
          marginTop: 4, fontSize: 12, color: "#3A6845", fontWeight: 500,
          whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        }}>
          {fileName}
        </div>
      )}
      <div style={{ marginTop: 4, fontSize: 13, color: C.textSec }}>
        Got it. Jobs are being matched now.
      </div>
      <button
        type="button"
        onClick={onClose}
        style={{
          marginTop: 16, width: "100%", padding: "11px 16px",
          background: C.cta, color: "#fff", border: "none",
          borderRadius: 12, cursor: "pointer",
          fontSize: 14, fontWeight: 700, fontFamily: FF,
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.ctaHov }}
        onMouseLeave={(e) => { e.currentTarget.style.background = C.cta }}
      >
        Done
      </button>
    </div>
  )
}

function ErrorCard({ message, onRetry, onClose }) {
  return (
    <div className="arm-pop" style={{
      padding: 20, background: "#FFF4F4",
      border: "1px solid #FECACA", borderRadius: 20,
    }}>
      <div style={{
        width: 46, height: 46, borderRadius: "50%",
        background: "rgba(255,68,68,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="24" height="24" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="8" stroke="#CC2222" strokeWidth="1.6" />
          <path d="M10 6v4M10 14h.01" stroke="#CC2222" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <div style={{ marginTop: 16, fontSize: 16, fontWeight: 700, color: C.text }}>
        Something went wrong
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: C.textSec, lineHeight: 1.5 }}>
        {message}
      </div>
      <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
        <button
          type="button"
          onClick={onRetry}
          style={{
            flex: 1, padding: "11px 16px",
            background: C.cta, color: "#fff", border: "none",
            borderRadius: 12, cursor: "pointer",
            fontSize: 13, fontWeight: 700, fontFamily: FF,
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.ctaHov }}
          onMouseLeave={(e) => { e.currentTarget.style.background = C.cta }}
        >
          Try again
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            padding: "11px 18px",
            background: "transparent", color: C.textSec,
            border: `1px solid ${C.border}`,
            borderRadius: 12, cursor: "pointer",
            fontSize: 13, fontWeight: 600, fontFamily: FF,
            transition: "background 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = C.card; e.currentTarget.style.color = C.text }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = C.textSec }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
